import { Component, OnInit, ViewEncapsulation, ViewChild, Input, Output, EventEmitter, ViewChildren, HostListener } from '@angular/core';
import { Router } from '@angular/router';

import { Subject, interval, ReplaySubject } from 'rxjs';
import { filter, map, timeout, take, tap, first } from 'rxjs/operators';

import { AutoSub, AutoSubOnDestroy } from '../../providers/auto-sub';
import { UserService } from '../../providers/user.service';
import { UserPositionService } from '../../providers/user-position.service';
import { MoteEventService } from '../../providers/mote-event.service';

import { MoteEvent, MoteEventType, LoveOptions } from '../../models';
import { User, Mote, MoteState, Mutex } from '../../models';

import { VirtualSwiperComponent } from '../virtual-swiper/virtual-swiper.component';
import { Slide } from '../virtual-swiper/virtual-swiper-model';
import { MapMode } from '../../providers/map-overlay.service';

@AutoSubOnDestroy()
@Component({
    selector: 'motes-swiper',
    templateUrl: './motes-swiper.component.html',
    styleUrls: ['./motes-swiper.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class MotesSwiperComponent implements OnInit {

    @Input() set motesInput(motes: Mote[]) {
        this.swiperMotes = motes;
        this.isLoading = motes && motes.length > 0 ? false : true;
        this.emitMoteEvent({ type: MoteEventType.SWIPER_MOTES_UPDATED });
    };

    /**
    * Two way binding of swiper open state
    */
    @Input() set openSwiper(open: boolean) {
        this.setSwiperOpend(open);
    }

    @Input() set disableTouch(disable: boolean) {
        // Enable paren to disable touch of swiper (this gives a significant performance improvement on overlay pages with scroll)
        this.doDisableTouch = disable;
    }

    /**
     * Listen to keyboard events and prevent default behavior (should only be enabled on swiper pages)
     */
    @Input() enableKeyboardShortcuts: boolean = false;

    @Input('mapMode') set setMapMode(mapMode: MapMode) {
        this.mapMode = mapMode;
        if(mapMode != MapMode.DEFAULT) {
            this.SLIDE_PCT_WIDTH = 0.75;
            this.SLIDE_MAX_PCT_HEIGHT = 0.35;
            this.SLIDE_WIDTH_HEIGHT_RATIO = 0.7;
        }
        if(mapMode == MapMode.STORY_EXPLORER) this.SLIDE_WIDTH_HEIGHT_RATIO = 0.75;
    }

    @HostListener('window:resize', ['$event']) onResize(event) {
        this.updateMoteSize(event.target.innerWidth, event.target.innerHeight);
    }

    @Output() openSwiperChanged = new EventEmitter<boolean>();

    // View children
    @ViewChild("motesSwiper") motesSwiper: VirtualSwiperComponent;
    @ViewChild("openCloseSwiper", { static: true }) openCloseSwiper: VirtualSwiperComponent;

    // Slide animation timing
    public readonly MOTE_SLIDE_TIME = 220;
    // Interval to poll for mote key in swiper item list
    public readonly MOTE_POLL_INTERVAL = 300;
    // OBS! For some reason the open slide time must be smaller than MOTE_POLL_INTERVAL,
    // else the animation will be interrupted. This insure that swiper will open before starting
    // mote scroll sequence. TODO: Why can't we scroll at the same time anymore? - why, oh why?
    public readonly OPEN_SLIDE_TIME = 250;
    public readonly MULTI_SLIDE_TIME = 200;

    // Consts
    private SLIDE_PCT_WIDTH = 0.9;
    private SLIDE_MAX_WIDTH = 470;
    private readonly SLIDE_MIN_WIDTH = 200;

    // Max pct that slide is allowed to fill screen before defining the width using height as max
    private SLIDE_MAX_PCT_HEIGHT = 0.5;
    private SLIDE_WIDTH_HEIGHT_RATIO = 0.8;

    private debugPositionCounter: number = 0;

    public isSwiperOpen: boolean = false;

    public swiperMotes: Mote[] = [];
    public mapMode: MapMode = MapMode.DEFAULT;

    public user: User = null;
    public selectedMoteKey = '';
    public doDisableTouch: boolean = false;

    private initSubject: ReplaySubject<void> = new ReplaySubject(1);

    private focusDebounceSubject: Subject<MoteEvent> = new Subject();
    private swiperMutex = new Mutex();

    public motesSwiperIndex: number = 0;
    public isLoading = true;
    public slideWidth = 300;
    public slideMargin = -20;
    public slideHeight = 400;

    // Expose to view
    public MapMode = MapMode;

    constructor(private userService: UserService, private moteEventService: MoteEventService, private userPositionService: UserPositionService, private router: Router) {
        // this.enableFocusDebounce();
    }

    ngOnInit() {

        // Subscribe for mote events from outside
        AutoSub(this).reg['moteEventSub'] = this.moteEventService.watchMoteEvents().subscribe(ev => {
            this.handleMoteEvent(ev);
        });

        // Subscribe for active user (in order to save love state of motes)
        AutoSub(this).reg['userSub'] = this.userService.watchCurrentUser().subscribe(user => {
            this.user = user;
        });
        // Update size of swiper / mini mote slide
        this.updateMoteSize(window.innerWidth, window.innerHeight);
    }

    ngAfterViewInit() {
        this.initSubject.next();
    }

    private awaitInit(): Promise<void> {
        return this.initSubject.pipe(first()).toPromise();
    }

    /**
     * Main loop for handling incoming mote events
     */
    private async handleMoteEvent(ev: MoteEvent) {
        switch(ev.type) {
            case MoteEventType.LOVE_CHANGED:
                if(!this.user) return console.warn("Cannot save user mote love, no user");
                this.userService.saveVoteState(ev.mote.key, this.user.key, (ev.options as LoveOptions).isLoved);
                break;
            case MoteEventType.SWIPE_TO_MOTE:
            case MoteEventType.MAP_MOTE_SELECTED:
                this.selectedMoteKey = ev.mote.key;
                this.setSwiperOpend(true);
                this.doOpenSwiper(true);
                await this.swipeToMote(ev.mote.key);
                break;
            case MoteEventType.SKIP_TO_MOTE:
                this.selectedMoteKey = ev.mote.key;
                await this.swipeToMote(ev.mote.key);
                this.setSwiperOpend(true);
                this.doOpenSwiper(true);
                break;
            case MoteEventType.OPEN_UNLOCKED_MOTE:
                // Do nothing
                break;
            case MoteEventType.SWIPER_MOTE_FOCUS:
                this.doOpenSwiper(true);
                break;
            case MoteEventType.SELECT_ACTIVE_ROUTE:
            case MoteEventType.SHOW_MOTE_ON_MAP:
            case MoteEventType.UNLOCK_STARTED:
            case MoteEventType.UNLOCK_MOTE:
            case MoteEventType.MAP_MOTE_UNSELECTED:
            case MoteEventType.NAVIGATE_TO_MOTE:
            case MoteEventType.UNLOCK_DONE:
            case MoteEventType.INACTIVE_SELECTED:
            case MoteEventType.SHOW_3D_MOTE_ON_MAP:
            case MoteEventType.SWIPER_CLOSED:
            case MoteEventType.MEDIA_CONSUMED:
            case MoteEventType.MOTE_FILTER_UPDATED:
                this.doOpenSwiper(false);
                break;
        }
    }

    /**
     * Calculate and update the size of the motes slides (and mini mote) on init and on resize events
     */
    private updateMoteSize(viewWidth: number, viewHeight: number) {
        // Calc slide width from view vidth and clamp it
        let width = this.clamp(viewWidth * this.SLIDE_PCT_WIDTH, this.SLIDE_MIN_WIDTH, this.SLIDE_MAX_WIDTH);
        // Find height using width
        let height = Math.round(width * this.SLIDE_WIDTH_HEIGHT_RATIO);
        // Fall back to using height as max if screen is width and not very heigh
        if(height >= viewHeight * this.SLIDE_MAX_PCT_HEIGHT || viewHeight <= 400) {
            // Calculate width determined from height and clamp to min width
            height = Math.round(viewHeight * this.SLIDE_MAX_PCT_HEIGHT);
            // Width / height ratio of minimote (hiding details whren screen is smaller than 400)  
            let miniMoteRatio = (viewHeight <= 400) ? (16 / 10) : (1 / this.SLIDE_WIDTH_HEIGHT_RATIO);
            // Calc width and clamp value
            width = this.clamp(height * miniMoteRatio, this.SLIDE_MIN_WIDTH, this.SLIDE_MAX_WIDTH);
        }

        // Add 'magic' 30 px to make sure swiper container does not cut top of mote on iPhone 5 / SE
        this.slideHeight = height + 30;
        this.slideWidth = width;
        this.slideMargin = - Math.round((width * 0.05) / 2) * 2;
    }

    /**
     * Clamp a value to the given min / max
     */
    private clamp(value: number, min: number, max: number): number {
        // Clamp to minviewWidth
        value = value < min ? min : value;
        // Clamp to max
        value = value > max ? max : value;
        return Math.round(value);
    }

    /**
     * Watch the swiper (virtual sc[takeFocusOnKeyChange]="true"roll) view child (however this should only change once after view init)
     */
    // private watchSwiper(): Observable<VirtualSwiperComponent> {
    //     return this.motesSwiper.changes.pipe(filter(queryList => queryList.first), map(querList => querList.first));
    // }

    private async doOpenSwiper(open: boolean) {
        // Await view init and to get swiper lock
        await this.awaitInit();
        await this.swiperMutex.lock();

        // Open / close swiper
        await this.openCloseSwiper.scrollToSlideKey(open ? 'open' : 'close')

        // Release lock
        this.swiperMutex.unlock();
    }

    /**
     * Change the swiper open state from the view (onActiveKeyChanged)
     */
    public swiperOpened(slideKey: string) {
        let isOpen = (slideKey == 'open');
        this.setSwiperOpend(isOpen);
        // Emit open event to listeners
        let evType = isOpen ? MoteEventType.SWIPER_OPENED : MoteEventType.SWIPER_CLOSED;
        this.emitMoteEvent({ type: evType });
    }

    /**
     * Toggle swiper open state and emit angular component event if updated
     */
    private async setSwiperOpend(open: boolean) {
        if(this.isSwiperOpen == open) return;
        this.isSwiperOpen = open;
        this.openSwiperChanged.emit(open);
    }

    private async swipeToMote(moteKey: string): Promise<void> {
        await this.awaitInit();

        // Queue up swipe events (await swiper to finish any previous swipe events)
        //await this.swiperMutex.lock();
        // Try to scroll to mote (in the now unlikely event that mote dosent exist yet, try again for 3 sec)
        let success = await this.pollSwipeToMote(moteKey).catch((err) => false);
        if(!success) console.warn(`Could not swipe to mote key: '${moteKey}', it dosent exist in swiper list?"`, this.swiperMotes.map(m => m ? m.key : 'null mote?'));
        // Release the lock to allow swiping to other motes
        //this.swiperMutex.unlock();
    }

    private pollSwipeToMote(moteKey: string): Promise<boolean> {
        // Filter out negative results (mark success if swiped to mote or the user changed the swipe target)
        let swipeSuccess = filter<boolean>((swipedToMote: boolean) => swipedToMote || moteKey != this.selectedMoteKey);
        // Return true if mote key have been swiped to
        let swipeToMote = map<number, boolean>(t => this.motesSwiper.scrollToSlideKey(moteKey));
        // Poll mote key and throw error after 5 sec if not found
        return interval(this.MOTE_POLL_INTERVAL).pipe(swipeToMote, swipeSuccess, take(1), timeout(4000)).toPromise();
    }

    public activeMoteChanged(slide: Slide) {
        let moteKey = slide ? slide.key : '';
        let mote = this.swiperMotes.find(mote => mote.key == moteKey);

        // Test if this is based on a manual (mouse/touch) event or programatic
        let isProgramaticChange = (moteKey == this.selectedMoteKey || !this.isSwiperOpen);
        this.selectedMoteKey = moteKey;

        // If this was as manual focus change event (from user interaction), notify listners
        if(!isProgramaticChange) {
            this.emitMoteEvent({ type: MoteEventType.SWIPER_MOTE_FOCUS, mote: mote });
            //this.focusDebounceSubject.next({ type: MoteEventType.SWIPER_MOTE_FOCUS, mote: mote });
        }
    }

    public miniMoteIconClicked(mote: Mote) {
        // Increment debug position counter - allow position emulation when icon pressed 7 times 
        this.debugPositionCounter++;

        if(!this.userPositionService.enableLocationEmulation && this.debugPositionCounter <= 5) return;
        // Emulate user position for debugging purposes if enabled
        this.userPositionService.setUserPosition(mote.position);
    }

    // private enableFocusDebounce() {
    //     AutoSub(this).reg['debounceTimer'] = this.focusDebounceSubject.pipe(debounceTime(0)).subscribe((ev: MoteEvent) => {
    //         this.emitMoteEvent(ev);
    //     });
    // }

    /**
    * Collect all events from list of mini-mote components and forward them
    * @param ev : MoteEvent
    */
    public emitMoteEvent(ev: MoteEvent) {
        this.moteEventService.emitMoteEvent(ev);
    }

    /**
     * Emit swiper mote in focus clicked
     */
    public moteClicked(tourKey: string) {
        if(this.mapMode != MapMode.TOUR_EXPLORER || tourKey != this.selectedMoteKey) return;
        let mote = this.swiperMotes.find((mote) => mote.key == tourKey);
        this.emitMoteEvent({ mote: mote, type: MoteEventType.SELECTED_SWIPER_MOTE_CLICKED })
    }

    /**
     * Prevent pinch zoom on iOS
     */
    preventPinchZoom(ev: any): boolean {
        if(ev.scale && ev.scale != 1) ev.preventDefault();
        return true;
    }
}
